/**
 * useGarmentLookup - Garment lookup via QR code
 *
 * IMPORTANT: Uses utils.fetch() for imperative lookup because:
 * 1. Lookup is triggered by user action (scan), not component mount
 * 2. Prevents stale data issues with reactive queries
 * 3. Better error handling and loading state management
 *
 * Note: Backend has getGarmentByQR as a query, so we use trpc utils
 * to call it imperatively rather than reactively.
 */
import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { trpc } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';
import { isExpectedTrpcError } from '@/lib/trpc-error-utils';
import { shortBuildId } from '@/lib/version-skew';

/**
 * Above this, a scan is slow enough that the floor notices it and it is worth a
 * Sentry event. Below it we only leave a breadcrumb — a captureMessage per scan
 * would flood the quota from a busy station.
 */
const SLOW_SCAN_MS = 2000;

/**
 * Sentry quota guard: at a high-latency site (e.g. Thread Lab's China path), a
 * "slow scan" can be the norm rather than the exception. Cap captureMessage
 * volume per page session so a persistently slow site can't flood the quota —
 * breadcrumbs stay uncapped, and the first N events per session are enough to
 * surface the signal.
 */
const MAX_SLOW_SCAN_REPORTS_PER_SESSION = 5;
let slowScanReportCount = 0;

// Type for the lookup result (from backend)
export interface GarmentLookupResult {
  garmentUnit: {
    id: string;
    sku: string;
    productName: string;
    variantName: string | null;
    size: string | null;
    color: string | null;
    status: string;
    itemIndex: number;
    totalInOrder: number;
    isRedo: boolean;
    redoReason: string | null;
    imageUrl: string | null;
    customPreviewUrl: string | null;
    customPreviewSourceUrl: string | null;
    /**
     * BLU-1018 preview facts. Field names and meanings are the shared
     * `ProductionPreviewFacts` contract in
     * `@/components/production/ProductionPreviewTile`, so a garment unit can be
     * handed straight to `buildFinishedGarmentPreviewEntries` — do not rename
     * them here. Mockups are the design rendered ON the garment and are what
     * the card shows; the front/back preview urls are the bare print files,
     * supplied but deliberately not rendered at a garment station. Everything
     * is nullable because each one is an org mapping that may simply not
     * exist.
     */
    isCustomProduct: boolean;
    frontMockupPreviewUrl: string | null;
    backMockupPreviewUrl: string | null;
    frontPreviewUrl: string | null;
    backPreviewUrl: string | null;
    hasFront: boolean;
    hasBack: boolean;
    caughtAt: string | null;
  };
  order: {
    id: string;
    orderName: string;
    customerName: string | null;
    totalPodItems: number;
    totalPapItems: number;
    itemsCaught: number;
    binId: string | null;
    binLabel: string | null;
    isSingleLine: boolean;
    orderDate?: string | null;
    batchNumber?: number | null;
  };
  binDirective: {
    // BLU-197: ASSIGN_BENCH / PLACE_ON_BENCH route to a work bench instead of a bin.
    action: 'PACKING_SHELF' | 'PLACE_IN_BIN' | 'ASSIGN_BIN' | 'ASSIGN_BENCH' | 'PLACE_ON_BENCH';
    binLabel: string | null;
    binId: string | null;
    binSize?: string;
    /** BLU-197: true when the assigned/required container is a bench, not a bin */
    isBench?: boolean;
    /** Single-line order for a client set to Hand over: send to the hand over bench. */
    handover?: true;
  };
  alreadyCaught?: boolean;
}

export function useGarmentLookup() {
  const [data, setData] = useState<GarmentLookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { selectedLocation } = useLocation();

  const utils = trpc.useUtils();

  const lookup = useCallback(async (qrCode: string) => {
    if (!selectedLocation?.id) {
      setError('No location selected');
      return;
    }

    // Clear previous state
    setError(null);
    setIsLoading(true);

    // performance.now() is monotonic: Date.now() can jump if the station clock
    // syncs mid-scan, which would fabricate a "slow" or negative duration.
    const startedAt = performance.now();
    const buildId = __BUILD_ID__;

    // durationMs spans react-query's retry/backoff policy (up to ~7s on a
    // transient failure) and tRPC httpBatchLink batching — it is
    // total-perceived time, not single-request wire time. Per-attempt timing
    // and a correlation id are follow-up work.
    try {
      const result = await utils.catcher.getGarmentByQR.fetch({
        qrCode,
        locationId: selectedLocation.id,
      });
      const durationMs = Math.round(performance.now() - startedAt);

      // Operator state is set BEFORE instrumentation runs below: a throwing
      // console/Sentry call must never turn a successful lookup into an
      // operator-facing error.
      setData(result as GarmentLookupResult);
      setError(null);

      try {
        // Deliberately console.log, NOT logger.info: the frontend logger only
        // writes to the console when MODE === 'development', and the whole point
        // is that an operator or Brett can read this off a production station.
        // NEVER log qrCode — it is a base64 THP token identifying a garment unit.
        console.log('⏱️ scan lookup', {
          durationMs,
          ok: true,
          online: navigator.onLine,
          build: shortBuildId(buildId),
        });

        if (durationMs > SLOW_SCAN_MS) {
          if (slowScanReportCount < MAX_SLOW_SCAN_REPORTS_PER_SESSION) {
            slowScanReportCount++;
            Sentry.captureMessage('Slow scan lookup', {
              level: 'warning',
              extra: { durationMs, online: navigator.onLine, buildId },
            });
          }
        } else {
          Sentry.addBreadcrumb({
            category: 'scan',
            level: 'info',
            message: 'scan lookup',
            data: { durationMs },
          });
        }
      } catch {
        // Instrumentation must never fail a successful lookup.
      }
    } catch (err) {
      const durationMs = Math.round(performance.now() - startedAt);
      const message = err instanceof Error ? err.message : 'Failed to look up garment';

      // Operator state is set BEFORE instrumentation runs below, same reasoning
      // as the success path above.
      setData(null);
      setError(message);

      try {
        console.log('⏱️ scan lookup', {
          durationMs,
          ok: false,
          online: navigator.onLine,
          build: shortBuildId(buildId),
        });

        // By-design tRPC guards (validation, precondition, conflict, …) are
        // not Sentry-worthy errors — see isExpectedTrpcError. They still leave
        // a breadcrumb so a slow-scan trail isn't silently missing an entry.
        if (!isExpectedTrpcError(err)) {
          Sentry.captureMessage('Scan lookup failed', {
            level: 'warning',
            extra: { durationMs, online: navigator.onLine, buildId, error: message },
          });
        } else {
          Sentry.addBreadcrumb({
            category: 'scan',
            level: 'warning',
            message: 'scan lookup failed (expected)',
            data: { durationMs, online: navigator.onLine, buildId },
          });
        }
      } catch {
        // Instrumentation must never mask the error already set above.
      }
    } finally {
      setIsLoading(false);
    }
  }, [utils.catcher.getGarmentByQR, selectedLocation?.id]);

  const clear = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return {
    data,
    error,
    isLoading,
    lookup,
    clear,
  };
}
